import { DeepMap } from 'react-hook-form';
import {
  JobsiteOnboardingFieldInput,
  JobsiteOnboardingStepInput,
  OnboardingStepKey,
} from 'apollo/generated/client-operations';
import {
  FormInputTypes,
  getUpdateInputValueFunction,
  GridColSpan,
  TypedFormInputs,
} from 'components/form';
import { toggleBorderClasses } from 'containers/jobsiteConfiguration/helpers/utils';
import {
  EditJobsiteConfigWorkerInfoField,
  EditJobsiteConfigWorkerInfoTabFormData,
} from 'containers/jobsiteConfiguration/types';
import { WorkerInfoField } from 'containers/jobsiteConfiguration/components/workerInfoField';
import { typedComponentProps } from 'utils';
import { Jobsite } from './types';

export const workerInfoSectionInputs: TypedFormInputs<EditJobsiteConfigWorkerInfoTabFormData> =
  {
    email: {
      element: FormInputTypes.CustomInput,
      label: 'Email Address',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { customInput: WorkerInfoField },
    },
    birthDate: {
      element: FormInputTypes.CustomInput,
      label: 'Date of Birth',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { customInput: WorkerInfoField },
    },
    ssnLastFour: {
      element: FormInputTypes.CustomInput,
      label: 'Last 4 Digits of SSN',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { customInput: WorkerInfoField },
    },
    race: {
      element: FormInputTypes.CustomInput,
      label: 'Race',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { customInput: WorkerInfoField },
    },
    gender: {
      element: FormInputTypes.CustomInput,
      label: 'Gender',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { customInput: WorkerInfoField },
    },
    trade: {
      element: FormInputTypes.CustomInput,
      label: 'Trade',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { customInput: WorkerInfoField },
    },
    tradeExperience: {
      element: FormInputTypes.CustomInput,
      label: 'Trade Experience',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { customInput: WorkerInfoField },
    },
    title: {
      element: FormInputTypes.CustomInput,
      label: 'Title',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { customInput: WorkerInfoField },
    },
    classificationGroup: {
      element: FormInputTypes.CustomInput,
      label: 'Classification Group',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { customInput: WorkerInfoField },
    },
    emergencyContact: {
      element: FormInputTypes.CustomInput,
      label: 'Emergency Contact',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { customInput: WorkerInfoField },
    },
    address: {
      element: FormInputTypes.CustomInput,
      label: 'Address',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { customInput: WorkerInfoField },
    },
    addressZipCode: {
      element: FormInputTypes.CustomInput,
      label: 'Address Zip Code',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { customInput: WorkerInfoField },
    },
    unionAffiliation: {
      element: FormInputTypes.CustomInput,
      label: 'Union Affiliation',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { customInput: WorkerInfoField },
    },
    veteranStatus: {
      element: FormInputTypes.CustomInput,
      label: 'Veteran Status',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: { customInput: WorkerInfoField },
    },
    citizenshipStatus: {
      element: FormInputTypes.CustomInput,
      label: 'Citizenship Status',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: {
        customInput: WorkerInfoField,
        ...typedComponentProps<typeof WorkerInfoField>({ showEnforced: true }),
      },
    },
    workAuthorizationStatus: {
      element: FormInputTypes.CustomInput,
      label: 'Work Authorization Status',
      layout: [GridColSpan.SpanFull, toggleBorderClasses],
      elementProps: {
        customInput: WorkerInfoField,
        ...typedComponentProps<typeof WorkerInfoField>({ showEnforced: true }),
      },
    },
  };

export const getWorkerInfoSectionDefaultValues = (
  jobsite: Jobsite,
): EditJobsiteConfigWorkerInfoTabFormData => {
  const workerInfoStep = jobsite?.onboardingModule?.steps?.find(
    (s) => s.key === OnboardingStepKey.PersonalInfo,
  );
  const { fields } = workerInfoStep ?? {};

  const getWorkerInfoField = (
    fieldKey: keyof EditJobsiteConfigWorkerInfoTabFormData,
  ): EditJobsiteConfigWorkerInfoField => {
    const { isRequired, isHidden, isEnforced } =
      fields?.find((f) => f.key === fieldKey) ?? {};
    return {
      isRequired: !!isRequired,
      isHidden: !!isHidden,
      isEnforced: !!isEnforced,
    };
  };

  return {
    email: getWorkerInfoField('email'),
    birthDate: getWorkerInfoField('birthDate'),
    ssnLastFour: getWorkerInfoField('ssnLastFour'),
    race: getWorkerInfoField('race'),
    gender: getWorkerInfoField('gender'),
    trade: getWorkerInfoField('trade'),
    tradeExperience: getWorkerInfoField('tradeExperience'),
    classificationGroup: getWorkerInfoField('classificationGroup'),
    title: getWorkerInfoField('title'),
    emergencyContact: getWorkerInfoField('emergencyContact'),
    address: getWorkerInfoField('address'),
    addressZipCode: getWorkerInfoField('addressZipCode'),
    veteranStatus: getWorkerInfoField('veteranStatus'),
    citizenshipStatus: getWorkerInfoField('citizenshipStatus'),
    unionAffiliation: getWorkerInfoField('unionAffiliation'),
    workAuthorizationStatus: getWorkerInfoField('workAuthorizationStatus'),
  };
};

type WorkerInfoSectionUpdateInput = {
  personalInfoStep: JobsiteOnboardingStepInput;
};

export const getWorkerInfoSectionUpdateInput = (
  workerInfo: EditJobsiteConfigWorkerInfoTabFormData,
  dirtyFields: DeepMap<EditJobsiteConfigWorkerInfoTabFormData, true>,
): WorkerInfoSectionUpdateInput => {
  const getUpdateInputValue = getUpdateInputValueFunction(
    workerInfo,
    dirtyFields,
  );

  const fields: JobsiteOnboardingFieldInput[] = Object.keys(workerInfo)
    .map((key) => ({
      key,
      ...getUpdateInputValue(key as keyof typeof workerInfo),
    }))
    .filter(
      ({ isRequired, isHidden }) =>
        isRequired !== undefined || isHidden !== undefined,
    );

  return {
    personalInfoStep: fields.length
      ? {
          key: OnboardingStepKey.PersonalInfo,
          isVisible: true,
          fields,
        }
      : undefined,
  };
};
